import { LocalStorage } from "@/types/enums";
import { RouteTo } from "@routing/RouteMap";

export interface FrontendAuthState {
    isAuthenticated: boolean;
    isOnPublicPage: boolean;
    wasActivelyUsing: boolean;
    tokenExists: boolean;
    currentPath: string;
    authStateSource: 'token' | 'redux' | 'none';
}

/**
 * Check current frontend authentication state without requiring React hooks
 * This can be called from Apollo Client error handlers
 */
export const checkCurrentFrontendAuthState = (): FrontendAuthState => {
    const token = localStorage.getItem(LocalStorage.AUTH_TOKEN);
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
    
    // Define public pages where authentication is not required
    const publicPaths = [
        RouteTo.LOGIN,
        RouteTo.ONBOARDING,
        RouteTo.FORGOT_PASSWORD,
        RouteTo.RESET_PASSWORD,
        RouteTo.PRIVACY,
        RouteTo.AGB,
        RouteTo.CONTACT,
        RouteTo.USER_GUIDE,
        RouteTo.LEGAL,
        RouteTo.ABOUT
    ];
    
    // Check if current path is public or invite link
    const isOnPublicPage = publicPaths.includes(currentPath as any) || 
                          currentPath.startsWith(RouteTo.INVITE);
    
    // Check if token exists and appears valid (basic length check)
    const tokenExists = !!token;
    const tokenAppearValid = token && token.length > 18;
    
    // Determine auth state source
    let authStateSource: 'token' | 'redux' | 'none' = 'none';
    if (tokenAppearValid) {
        authStateSource = 'token';
    }
    
    // User was actively using the app if they're not on login-related pages
    const wasActivelyUsing = !currentPath.includes('/login') && 
                            !currentPath.includes('/onboarding') &&
                            !isOnPublicPage;
    
    const result: FrontendAuthState = {
        isAuthenticated: tokenAppearValid || false,
        isOnPublicPage,
        wasActivelyUsing,
        tokenExists,
        currentPath,
        authStateSource
    };
    
    console.log('🔍 Frontend auth state:', result);
    return result;
};

/**
 * Determine if an authentication error should trigger a logout vs redirect
 */
export const shouldPerformLogout = (authState: FrontendAuthState): boolean => {
    // If user appears authenticated and was actively using the app, logout is needed
    if (authState.isAuthenticated && authState.wasActivelyUsing) {
        return true;
    }
    
    // If user has a token but is on public page, still logout to clean state
    if (authState.tokenExists && !authState.isOnPublicPage) {
        return true;
    }
    
    // Otherwise, just redirect
    return false;
};

/**
 * Get appropriate action based on auth state and error context
 */
export type AuthErrorAction = 'logout' | 'silent_redirect' | 'informational_redirect';

export const determineAuthErrorAction = (
    authState: FrontendAuthState,
    operationName?: string
): AuthErrorAction => {
    // User was authenticated and actively using app → full logout
    if (shouldPerformLogout(authState)) {
        return 'logout';
    }
    
    // User on public page → silent redirect
    if (authState.isOnPublicPage) {
        return 'silent_redirect';
    }
    
    // User already logged out but trying to access protected content → informational
    return 'informational_redirect';
};

/**
 * Check if user is currently on login page
 */
export const isOnLoginPage = (): boolean => {
    return typeof window !== 'undefined' && 
           window.location.pathname === RouteTo.LOGIN;
};

/**
 * Check if we're in a development environment
 */
export const isDevelopmentMode = (): boolean => {
    return process.env.NODE_ENV === 'development';
};
